import React, { useState, useEffect } from 'react';
import { Play, FileText, Users, Crown, X } from 'lucide-react';
import { clsx } from 'clsx';

interface DemoScenario {
  title: string;
  description: string;
}

interface DemoScenarios {
  [key: string]: DemoScenario;
}

interface DemoModeProps {
  onStartDemo: (scenario: string) => void;
  onCompleteDemo: (conversationId: string) => void;
  onClose: () => void;
  isVisible: boolean;
  currentConversationId?: string;
}

export const DemoMode: React.FC<DemoModeProps> = ({
  onStartDemo,
  onCompleteDemo,
  onClose,
  isVisible,
  currentConversationId
}) => {
  const [scenarios, setScenarios] = useState<DemoScenarios>({});
  const [loading, setLoading] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<string>('premium_christmas');

  // Load available demo scenarios
  useEffect(() => {
    const loadScenarios = async () => {
      try {
        const response = await fetch('/api/demo-scenarios');
        const data = await response.json();
        setScenarios(data.scenarios);
      } catch (error) {
        console.error('Error loading demo scenarios:', error);
      }
    };

    if (isVisible) {
      loadScenarios();
    }
  }, [isVisible]);

  const handleStartDemo = async (scenario: string) => {
    setLoading(true);
    try {
      await onStartDemo(scenario);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteDemo = async () => {
    if (!currentConversationId) return;
    
    setLoading(true);
    try {
      await onCompleteDemo(currentConversationId);
    } finally {
      setLoading(false);
    }
  };

  const getScenarioIcon = (scenarioKey: string) => {
    switch (scenarioKey) {
      case 'premium_christmas':
        return <Crown size={24} className="text-purple-600" />;
      case 'budget_appreciation':
        return <Users size={24} className="text-green-600" />;
      case 'executive_premium':
        return <FileText size={24} className="text-blue-600" />;
      default:
        return <Play size={24} className="text-gray-600" />;
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Demo Mode</h2>
              <p className="text-purple-100">
                Experience the complete Sales Assistant workflow with realistic scenarios
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Demo Status */}
          {currentConversationId && currentConversationId.includes('demo_') && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-green-800">Demo in Progress</h3>
                  <p className="text-green-600 text-sm">
                    Continue chatting or generate the complete quotation
                  </p>
                </div>
                <button
                  onClick={handleCompleteDemo}
                  disabled={loading}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <FileText size={16} />
                  <span>{loading ? 'Generating...' : 'Complete Demo'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Demo Scenarios */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Choose a Demo Scenario
            </h3>

            {Object.entries(scenarios).map(([key, scenario]) => (
              <div
                key={key}
                className={clsx(
                  'border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md',
                  selectedScenario === key
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                )}
                onClick={() => setSelectedScenario(key)}
              >
                <div className="flex items-start space-x-3">
                  {getScenarioIcon(key)}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {scenario.title}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {scenario.description}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div
                      className={clsx(
                        'w-4 h-4 rounded-full border-2',
                        selectedScenario === key
                          ? 'border-purple-500 bg-purple-500'
                          : 'border-gray-300'
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Demo Features */}
          <div className="mt-8 bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">
              What You'll See in This Demo:
            </h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span>AI-powered conversation with realistic client scenario</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span>Automatic data collection for quotation (6 key fields)</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span>Product recommendations based on budget and occasion</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span>Professional quotation generation with multiple packages</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span>Mobile-responsive interface and admin features</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 mt-6">
            <button
              onClick={() => handleStartDemo(selectedScenario)}
              disabled={loading}
              className="flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex-1"
            >
              <Play size={18} />
              <span>{loading ? 'Starting...' : 'Start Demo'}</span>
            </button>

            <button
              onClick={onClose}
              className="flex items-center space-x-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors"
            >
              <span>Cancel</span>
            </button>
          </div>

          {/* Demo Tips */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h5 className="font-semibold text-blue-800 mb-2">Demo Tips:</h5>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• The demo will pre-populate conversation history</li>
              <li>• You can continue chatting naturally after the demo starts</li>
              <li>• Try the "Complete Demo" button to generate the full quotation</li>
              <li>• Test mobile view by resizing your browser window</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};